/**
 * Silent Accumulation Scanner — "Âm thầm thu gom chờ đánh break"
 *
 * Thin page container. Feature blocks live under:
 *   dashboard/src/components/silent-accumulation/
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Skeleton,
  Snackbar, Alert,
  ToggleButton, ToggleButtonGroup, TextField, InputAdornment,
  alpha,
} from '@mui/material';
import { Refresh, Search, ManageSearch, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { silentAccumulationAPI } from '../services/api';
import {
  StatTilesBlock,
  SummaryCardsBlock,
  TopPicksBlock,
  LeaderboardBlock,
  TickerDetailDrawerBlock,
  waitForRenderFrames,
  exportDrawerCapture,
} from '../components/silent-accumulation';
import {
  fmtScore, fmtPct, scoreColor, heatColor, earlyColor, stretchColor, rsiColor,
  metricSnapshot,
} from '../components/silent-accumulation/helpers';
import html2canvas from 'html2canvas';

export default function SilentAccumulation() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const T = (key, opts) => t(`silentAccumulation.${key}`, opts);

  // State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [style, setStyle] = useState('swing');
  const [sortCol, setSortCol] = useState('TotalAccumBreakScore');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [tickerDetail, setTickerDetail] = useState(null);
  const [tickerLoading, setTickerLoading] = useState(false);
  const [topPicks, setTopPicks] = useState(null);
  const [topPicksLoading, setTopPicksLoading] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const [exportingTopPicksImage, setExportingTopPicksImage] = useState(false);
  const topPicksExportRef = useRef(null);
  const drawerCaptureRef = useRef(null);
  const drawerScrollRef = useRef(null);

  // ── Scan handler ──
  const runScan = useCallback(async () => {
    setLoading(true);
    setTopPicksLoading(true);
    setError('');
    try {
      const [scanRes, picksRes] = await Promise.all([
        silentAccumulationAPI.scan({
          style,
          top_n: 80,
          min_accum_score: 35,
          max_dist_to_pivot: 12,
          max_base_width: 0.5,
          enrich_wyckoff: true,
          persist: true,
        }),
        silentAccumulationAPI.getTopPicks({ style, top_n: 8 }),
      ]);
      setData(scanRes.data);
      setTopPicks(picksRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Scan failed');
    } finally {
      setLoading(false);
      setTopPicksLoading(false);
    }
  }, [style]);

  // Load on mount
  useEffect(() => { runScan(); }, [runScan]);

  // ── Ticker detail ──
  const openDetail = useCallback(async (ticker) => {
    setSelectedTicker(ticker);
    setTickerLoading(true);
    try {
      const res = await silentAccumulationAPI.getTicker(ticker, { style, days: 30 });
      setTickerDetail(res.data);
    } catch {
      setTickerDetail(null);
    } finally {
      setTickerLoading(false);
    }
  }, [style]);

  const handleExportImage = useCallback(async () => {
    if (!drawerCaptureRef.current || !drawerScrollRef.current || !selectedTicker) return;
    setExportingImage(true);
    try {
      await exportDrawerCapture({
        captureNode: drawerCaptureRef.current,
        scrollNode: drawerScrollRef.current,
        fileName: `silent-accumulation-${selectedTicker.toLowerCase()}.png`,
        backgroundColor: '#ffffff',
      });
    } finally {
      setExportingImage(false);
    }
  }, [selectedTicker]);

  const handleExportTopPicksImage = useCallback(async () => {
    if (!topPicksExportRef.current || exportingTopPicksImage || !topPicks?.top_picks?.length) return;
    setExportingTopPicksImage(true);
    let wrapper = null;
    try {
      await waitForRenderFrames(2);
      const sourceNode = topPicksExportRef.current;
      const clone = sourceNode.cloneNode(true);
      wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.padding = '18px 20px 20px';
      wrapper.style.background = '#ffffff';
      wrapper.style.boxSizing = 'border-box';
      wrapper.style.width = `${Math.ceil(sourceNode.getBoundingClientRect().width) + 40}px`;
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      const asOfDate = data?.scan_date?.split(' ')[0] || new Date().toISOString().slice(0, 10);
      link.download = `silent-accumulation-top-picks-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      setError(e?.message || 'Export image failed');
    } finally {
      wrapper?.remove();
      setExportingTopPicksImage(false);
    }
  }, [data?.scan_date, exportingTopPicksImage, topPicks]);

  // ── Sorting ──
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  // i18n label helpers
  const labelText = (label) => ({
    break_imminent: T('labels.breakImminent'),
    break_ready: T('labels.breakReady'),
    accumulating: T('labels.accumulating'),
    none: T('labels.none'),
  }[label] || T('labels.none'));

  // ── Derived data ──
  const results = data?.results || [];
  const summary = data?.summary || {};

  // Filter + sort
  const filtered = results
    .filter(r => !searchTerm || r.ticker?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const va = a[sortCol] ?? 0;
      const vb = b[sortCol] ?? 0;
      return sortDir === 'desc' ? vb - va : va - vb;
    });

  // Chart data for distribution of labels
  const chartData = [
    { name: T('chart.aboutToBreakout'), value: summary.break_imminent || 0, color: '#ef4444' },
    { name: T('chart.breakReady'), value: summary.break_ready || 0, color: '#f59e0b' },
    { name: T('chart.accumulating'), value: summary.accumulating || 0, color: '#3b82f6' },
  ];

  const isFirstLoad = loading && !data;
  const detailLatest = tickerDetail?.latest || null;
  const detailBriefing = tickerDetail?.briefing || null;
  const detailCompanyName = detailLatest?.company_name || selectedTicker;
  const detailLabel = detailLatest?.accum_break_label ? labelText(detailLatest.accum_break_label) : T('labels.none');
  const detailPhase = tickerDetail?.wyckoff?.phase ? t(`phase.phases.${tickerDetail.wyckoff.phase}`, tickerDetail.wyckoff.phase) : T('labels.none');
  const detailInsight = detailBriefing?.insight || null;
  const detailReasons = Array.isArray(detailBriefing?.reasons) ? detailBriefing.reasons : [];
  const detailWatchouts = Array.isArray(detailBriefing?.watchouts) ? detailBriefing.watchouts : [];
  const detailSnapshotItems = detailLatest ? [
    {
      label: T('scores.score'),
      ...metricSnapshot(detailLatest.metric_snapshot_view?.mf_score, { value: fmtScore(detailLatest.score), color: scoreColor(detailLatest.score) }),
    },
    {
      label: T('dialog.entryHeat'),
      ...metricSnapshot(detailLatest.metric_snapshot_view?.entry_heat, { value: fmtScore(detailLatest.entry_heat_score ?? detailLatest.risk_score), color: heatColor(detailLatest.entry_heat_level, detailLatest.entry_heat_score ?? detailLatest.risk_score) }),
    },
    {
      label: T('dialog.adjustedScore'),
      ...metricSnapshot(detailLatest.metric_snapshot_view?.adjusted_score, { value: fmtScore(detailLatest.adjusted_score), color: scoreColor(detailLatest.adjusted_score) }),
    },
    {
      label: T('dialog.earlyScore'),
      ...metricSnapshot(detailLatest.metric_snapshot_view?.early_signal_score, { value: fmtScore(detailLatest.early_signal_score ?? detailLatest.EarlySignalScore), color: earlyColor(detailLatest.early_signal_score ?? detailLatest.EarlySignalScore) }),
    },
    {
      label: 'RSI',
      ...metricSnapshot(detailLatest.metric_snapshot_view?.rsi14, { value: fmtScore(detailLatest.rsi14), color: rsiColor(detailLatest.rsi14) }),
    },
    {
      label: T('dialog.stretchMa20'),
      ...metricSnapshot(detailLatest.metric_snapshot_view?.stretch_ma20, { value: fmtPct(detailLatest.stretch_ma20), color: stretchColor(detailLatest.stretch_ma20) }),
    },
  ] : [];
  const detailComponentItems = Array.isArray(detailLatest?.component_breakdown_view) ? detailLatest.component_breakdown_view.slice(0, 8) : [];
  const detailWyckoffSignals = Array.isArray(detailBriefing?.wyckoff_signal_items) ? detailBriefing.wyckoff_signal_items : [];
  const topPickRows = Array.isArray(topPicks?.top_picks) ? topPicks.top_picks : [];
  const topPickActionLabel = (pick) => {
    if (pick.accum_break_label === 'break_imminent') return T('topPicks.actions.breakoutWatch');
    if (pick.dist_to_pivot != null && pick.dist_to_pivot <= 3) return T('topPicks.actions.watchNearPivot');
    if ((pick.BreakReadinessScore || 0) >= 65) return T('topPicks.actions.waitConfirmation');
    return T('topPicks.actions.keepWatchlist');
  };

  let scanButtonText;
  if (loading) scanButtonText = T('scanning');
  else if (data) scanButtonText = T('rescan');
  else scanButtonText = T('scanNow');

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', p: { xs: 1.5, md: 3 } }}>
      {/* ── Header ────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
          }}>
            <ManageSearch sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {T('title')}
              </Typography>
              {data?.scan_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366f1', boxShadow: '0 0 6px #6366f1' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{data.scan_date.split(' ')[0]}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {T('subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Right: Controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={style}
            exclusive
            onChange={(_, v) => v && setStyle(v)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                fontSize: '0.72rem', fontWeight: 700, px: 1.5, textTransform: 'none',
                borderColor: '#e2e8f0', color: '#64748b',
                '&.Mui-selected': { bgcolor: alpha('#6366f1', 0.08), color: '#6366f1', borderColor: alpha('#6366f1', 0.3) },
              },
            }}
          >
            <ToggleButton value="swing">Swing</ToggleButton>
            <ToggleButton value="tplus">T+</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
            disabled={loading}
            onClick={runScan}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#6366f1', color: '#fff', px: 2,
              '&:hover': { bgcolor: '#4f46e5' },
              '&:disabled': { bgcolor: '#94a3b8', color: '#fff' },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}
          >
            {scanButtonText}
          </Button>
        </Box>
      </Box>

      {/* ── Stat Tiles ────────────────────────────────────────────── */}
      <StatTilesBlock isFirstLoad={isFirstLoad} data={data} summary={summary} T={T} />

      {/* ── Summary Cards — Signal Distribution + Wyckoff ─────── */}
      <SummaryCardsBlock isFirstLoad={isFirstLoad} data={data} summary={summary} chartData={chartData} t={t} T={T} />

      {/* ── Top Picks ─────────────────────────────────────────────── */}
      <TopPicksBlock topPicksLoading={topPicksLoading} topPicks={topPicks} topPickRows={topPickRows} topPicksExportRef={topPicksExportRef} exportingTopPicksImage={exportingTopPicksImage} handleExportTopPicksImage={handleExportTopPicksImage} lang={lang} t={t} T={T} labelText={labelText} topPickActionLabel={topPickActionLabel} openDetail={openDetail} />

      {/* ── Search ────────────────────────────────────────────────── */}
      {isFirstLoad ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Skeleton variant="rounded" width={200} height={34} sx={{ borderRadius: 2 }} />
          <Skeleton variant="text" width={100} height={14} />
          <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 3 }} />
        </Box>
      ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <TextField
          size="small"
          placeholder={T('search.placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
            },
          }}
          sx={{
            width: 220,
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem', borderRadius: 2,
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1' },
            },
          }}
        />
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
          <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>{filtered.length}</Box> / {results.length}
        </Typography>
      </Box>
      )}

      {/* ── Leaderboard Table ─────────────────────────────────────── */}
      <LeaderboardBlock isFirstLoad={isFirstLoad} filtered={filtered} data={data} sortCol={sortCol} sortDir={sortDir} handleSort={handleSort} openDetail={openDetail} labelText={labelText} t={t} T={T} />

      {/* ── Fail-fast rule ────────────────────────────────────────── */}
      {summary.fail_fast_rule && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha('#f59e0b', 0.06), borderRadius: 2, border: `1px solid ${alpha('#f59e0b', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>
            <Warning sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} /> {T('failFast')}: {summary.fail_fast_rule}
          </Typography>
        </Box>
      )}

      {/* ── Ticker Detail Drawer ─────────────────────────────────── */}
      <TickerDetailDrawerBlock selectedTicker={selectedTicker} setSelectedTicker={setSelectedTicker} drawerCaptureRef={drawerCaptureRef} drawerScrollRef={drawerScrollRef} exportingImage={exportingImage} handleExportImage={handleExportImage} tickerLoading={tickerLoading} tickerDetail={tickerDetail} detailLatest={detailLatest} detailBriefing={detailBriefing} detailCompanyName={detailCompanyName} detailLabel={detailLabel} detailPhase={detailPhase} detailInsight={detailInsight} detailReasons={detailReasons} detailWatchouts={detailWatchouts} detailSnapshotItems={detailSnapshotItems} detailComponentItems={detailComponentItems} detailWyckoffSignals={detailWyckoffSignals} lang={lang} t={t} T={T} />

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
